import { CSSProperties } from "react";

export type TopicKey = "ai" | "robotics" | "home" | "side";
export type PostType = "build-log" | "how-to" | "note";

export interface Topic {
  key: TopicKey;
  name: string;
  shortName: string;
  description: string;
  href: string;
  color: string;
  tint: string;
  /** Text in the topic colour. Side Quests uses ink: yellow text fails contrast on white. */
  textClass: string;
  labelClass: string;
  fillClass: string;
  borderClass: string;
  tintClass: string;
}

// Class names are spelled out in full so Tailwind's content scan keeps them.
export const TOPICS: Record<TopicKey, Topic> = {
  ai: {
    key: "ai",
    name: "Software & AI",
    shortName: "Software & AI",
    description: "Code, tools, and experiments with AI models and agents.",
    href: "/topics/ai",
    color: "#1f4bff",
    tint: "#e8edff",
    textClass: "text-ai",
    labelClass: "bg-ai text-white",
    fillClass: "bg-ai",
    borderClass: "border-ai",
    tintClass: "bg-ai-tint",
  },
  robotics: {
    key: "robotics",
    name: "Robotics & Electronics",
    shortName: "Robotics",
    description: "Arduino, Raspberry Pi, motors, sensors, RC.",
    href: "/topics/robotics",
    color: "#ff4f1a",
    tint: "#ffede6",
    textClass: "text-robotics",
    labelClass: "bg-robotics text-ink",
    fillClass: "bg-robotics",
    borderClass: "border-robotics",
    tintClass: "bg-robotics-tint",
  },
  home: {
    key: "home",
    name: "Home & Workshop",
    shortName: "Home & Workshop",
    description:
      "Renovation projects, before and after photos, and what they really cost.",
    href: "/topics/home",
    color: "#1a7f4b",
    tint: "#e3f3ea",
    textClass: "text-home",
    labelClass: "bg-home text-white",
    fillClass: "bg-home",
    borderClass: "border-home",
    tintClass: "bg-home-tint",
  },
  side: {
    key: "side",
    name: "Side Quests",
    shortName: "Side Quests",
    description: "Everything else I get curious about on weekends.",
    href: "/topics/side",
    color: "#ffc21a",
    tint: "#fff6d6",
    textClass: "text-ink",
    labelClass: "bg-side text-ink",
    fillClass: "bg-side",
    borderClass: "border-side",
    tintClass: "bg-side-tint",
  },
};

export const TOPIC_ORDER: TopicKey[] = ["ai", "robotics", "home", "side"];

export const POST_TYPES: Record<PostType, { label: string; plural: string }> = {
  "build-log": { label: "Build log", plural: "Build logs" },
  "how-to": { label: "How-to", plural: "How-tos" },
  note: { label: "Note", plural: "Notes" },
};

export const POST_TYPE_ORDER: PostType[] = ["build-log", "how-to", "note"];

/** CSS custom properties consumed by the prose, callout and TOC styles. */
export const topicStyle = (topic: TopicKey): CSSProperties =>
  ({
    "--topic": TOPICS[topic].color,
    "--topic-tint": TOPICS[topic].tint,
  } as CSSProperties);
